import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import FleetMap from "../components/FleetMap";
import { StatusBadge, STATUS_COLORS } from "../components/StatusBadge";
import {
  MagnifyingGlass, Path, Terminal, Bell, Heartbeat,
} from "@phosphor-icons/react";

const FILTERS = ["all", "moving", "idle", "stopped", "offline", "panic"];

function Info({ label, value, color }) {
  return (
    <div>
      <div className="label-caps text-[9px]">{label}</div>
      <div className="text-sm font-semibold" style={{ color }}>{value}</div>
    </div>
  );
}

export default function LiveTracking() {
  const [vehicles, setVehicles] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [pois, setPois] = useState([]);
  const [address, setAddress] = useState("");
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState("all");
  const [trails, setTrails] = useState({});
  const [follow, setFollow] = useState(false);
  const [tab, setTab] = useState("summary");
  const nav = useNavigate();

  useEffect(() => {
    const load = () => api.get("/vehicles/live").then((r) => {
      setVehicles(r.data.vehicles);
      setSelected((s) => s ? r.data.vehicles.find((v) => v.id === s.id) || s : s);
      setTrails((prev) => {
        const next = { ...prev };
        for (const v of r.data.vehicles) {
          const arr = next[v.id] ? [...next[v.id]] : [];
          const last = arr[arr.length - 1];
          if (!last || last[0] !== v.lat || last[1] !== v.lng) arr.push([v.lat, v.lng]);
          next[v.id] = arr.slice(-15);
        }
        return next;
      });
    }).catch(() => {});
    load();
    api.get("/pois").then((r) => setPois(r.data.pois)).catch(() => {});
    api.get("/groups").then((r) => setGroups(r.data.groups)).catch(() => {});
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!selected) { setAddress(""); return; }
    setAddress("Locating…");
    api.get(`/geocode?lat=${selected.lat}&lng=${selected.lng}`)
      .then((r) => setAddress(r.data.place)).catch(() => setAddress(""));
  }, [selected?.id]);

  const groupIds = group === "all" ? null : new Set((groups.find((g) => g.id === group)?.vehicle_ids) || []);
  const filtered = vehicles.filter((v) =>
    (!groupIds || groupIds.has(v.id)) &&
    (filter === "all" || v.status === filter) &&
    (!q || v.vehicle_number.toLowerCase().includes(q.toLowerCase()) || v.imei.includes(q))
  );

  const addToGroup = async (gid) => {
    if (!selected) return;
    try {
      await api.post(`/groups/${gid}/add/${selected.id}`);
      const g = groups.find((x) => x.id === gid);
      toast.success(`${selected.vehicle_number} added to ${g?.name || "group"}`);
      const r = await api.get("/groups"); setGroups(r.data.groups);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
  };

  return (
    <div className="flex h-full">
      {/* Vehicle list */}
      <div className="w-72 shrink-0 border-r border-border flex flex-col bg-card">
        <div className="p-3 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="font-display font-bold text-lg">Vehicles</span>
            <span className="text-xs text-muted-foreground">{filtered.length}</span>
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-muted-foreground" />
            <input
              data-testid="tracking-search"
              value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search MH15 / IMEI"
              className="w-full bg-background border border-border rounded-sm pl-7 pr-2 py-1.5 text-xs focus:ring-1 focus:ring-primary focus:outline-none"
            />
          </div>
          {groups.length > 0 && (
            <select data-testid="group-filter" value={group} onChange={(e) => setGroup(e.target.value)}
              className="w-full mt-2 bg-background border border-border rounded-sm px-2 py-1.5 text-xs focus:ring-1 focus:ring-primary focus:outline-none">
              <option value="all">All Groups</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{`${g.name} (${g.vehicle_ids.length})`}</option>)}
            </select>
          )}
          <div className="flex flex-wrap gap-1 mt-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`text-[10px] uppercase font-bold px-2 py-1 rounded-sm border ${
                  filter === f ? "border-primary bg-primary/15 text-white" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >{f}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map((v) => (
            <button
              key={v.id}
              data-testid={`vehicle-row-${v.vehicle_number}`}
              onClick={() => setSelected(v)}
              className={`w-full text-left px-3 py-2.5 border-b border-border/50 hover:bg-white/5 ${
                selected?.id === v.id ? "bg-primary/10" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{v.vehicle_number}</span>
                <StatusBadge status={v.status} />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-0.5">
                <span>{v.speed} km/h</span>
                <span>{v.driver_name}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Map + info card */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 relative">
          {selected && (
            <button data-testid="follow-toggle" onClick={() => setFollow((f) => !f)}
              className={`absolute top-3 left-3 z-[1000] flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full shadow ${follow ? "bg-primary text-white" : "bg-white text-[#17263a] border border-border"}`}>
              <Path size={14} weight="bold" /> {follow ? `Following ${selected.vehicle_number}` : "Follow vehicle"}
            </button>
          )}
          <FleetMap
            vehicles={filtered}
            pois={pois}
            trails={trails}
            followId={follow && selected ? selected.id : null}
            selectedId={selected?.id}
            onSelect={setSelected}
            center={selected ? [selected.lat, selected.lng] : [21.5, 78.9]}
            zoom={selected ? 11 : 5}
          />
        </div>
        {selected && (
          <div className="border-t border-border bg-card p-4" data-testid="vehicle-info-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="font-display font-black text-xl">{selected.vehicle_number}</span>
                <span className="ml-3"><StatusBadge status={selected.status} /></span>
              </div>
              <span className="text-xs text-muted-foreground font-mono">IMEI {selected.imei}</span>
            </div>
            <div className="mb-3 text-sm flex items-center gap-1.5" data-testid="live-address">
              <span className="label-caps text-[9px]">Location</span>
              <span className="font-semibold text-primary">{address || "—"}</span>
            </div>
            <div className="flex gap-1 mb-3 border-b border-border">
              {["summary", "sensors", "street", "navigate"].map((t) => (
                <button key={t} data-testid={`tooltip-tab-${t}`} onClick={() => setTab(t)}
                  className={`text-xs font-semibold px-3 py-1.5 -mb-px border-b-2 ${tab === t ? "border-primary text-white" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  {t === "summary" ? "Summary" : t === "sensors" ? "Sensors" : t === "street" ? "Street View" : "Navigate"}
                </button>
              ))}
            </div>
            {tab === "summary" && (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-3" data-testid="tab-summary">
                <Info label="Speed" value={`${selected.speed} km/h`} color={STATUS_COLORS[selected.status]} />
                <Info label="Status" value={selected.status} />
                <Info label="Distance Today" value={`${selected.today_distance} km`} />
                <Info label="Max Speed" value={`${selected.max_speed} km/h`} />
                <Info label="Engine Hrs" value={selected.engine_hours} />
                <Info label="Driver" value={selected.driver_name} />
              </div>
            )}
            {tab === "sensors" && (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-3" data-testid="tab-sensors">
                <Info label="Ignition" value={selected.ignition ? "ON" : "OFF"} color={selected.ignition ? "#34C759" : "#FF9500"} />
                <Info label="Main Power" value={selected.main_power ? "OK" : "CUT"} color={selected.main_power ? "#34C759" : "#FF3B30"} />
                <Info label="Battery" value={`${selected.battery_pct}%`} />
                <Info label="GPS" value={selected.gps_valid ? "Valid" : "No Fix"} />
                <Info label="Satellites" value={selected.satellites} />
                <Info label="GSM" value={selected.gsm_signal} />
                <Info label="Network" value={selected.network} />
                <Info label="Panic" value={selected.panic ? "ACTIVE" : "—"} color={selected.panic ? "#FF3B30" : undefined} />
              </div>
            )}
            {tab === "street" && (
              <div className="mb-3" data-testid="tab-street">
                <p className="text-xs text-muted-foreground mb-2">Open Google Street View at the vehicle's current position ({Number(selected.lat || 0).toFixed(5)}, {Number(selected.lng || 0).toFixed(5)}).</p>
                <a data-testid="street-view-link" href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${selected.lat},${selected.lng}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-sm">Open Street View</a>
              </div>
            )}
            {tab === "navigate" && (
              <div className="mb-3" data-testid="tab-navigate">
                <p className="text-xs text-muted-foreground mb-2">Get driving directions to this vehicle's live location.</p>
                <a data-testid="navigate-link" href={`https://www.google.com/maps/dir/?api=1&destination=${selected.lat},${selected.lng}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-sm">Navigate with Google Maps</a>
              </div>
            )}
            <div className="flex flex-wrap gap-2 items-center">
              {[["History", Path, () => nav("/history?v=" + selected.id)],
                ["Command", Terminal, () => nav("/commands?imei=" + selected.imei)],
                ["Alerts", Bell, () => nav("/alerts")],
                ["Health", Heartbeat, () => nav("/device-health")]].map(([label, Icon, fn]) => (
                <button key={label} onClick={fn}
                  data-testid={`vehicle-action-${label.toLowerCase()}`}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 border border-border rounded-sm hover:border-primary">
                  <Icon size={14} weight="bold" /> {label}
                </button>
              ))}
              {groups.length > 0 && (
                <select data-testid="add-to-group" value=""
                  onChange={(e) => { if (e.target.value) addToGroup(e.target.value); }}
                  className="text-xs font-semibold px-2 py-1.5 border border-border rounded-sm bg-card hover:border-primary">
                  <option value="">+ Add to group…</option>
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
